import React from 'react';
import { Link, withRouter } from 'react-router';
import PropTypes from 'prop-types';
import { Image, Rating, Icon, Popup } from 'semantic-ui-react';
import numeral from 'numeral';
import _ from 'lodash';
import { DyzioPrimaryButton } from 'dyzio-shared-components/lib/components/dyzio-buttons';
import InfluencerCategoryComponent from '../../components/influencer-category';
import TargetLink from '../../components/target-link';

const TEAM_DYZIO_ID = 'f14d3931-0e6a-42e8-ad3c-ae2ff07e3d96';
const ALLOWED_ORGS = [];

const dyzioPinkStyle = {
  color: '#fc4085',
};

const tiktokIconStyle = {
  width: 15,
  marginTop: 2,
};

const activeLinkStyle = {
  color: 'black',
  opacity: 1,
  display: 'block',
  borderBottom: '4px solid black',
  padding: '4px 0',
};

const simpleLinkStyle = {
  color: 'black',
  opacity: '0.5',
  display: 'block',
  borderBottom: '4px solid white',
  padding: '4px 0',
};

const tabStyle = {
  borderBottom: 'none',
  padding: 0,
  opacity: 1,
};

const ABBOTTLYON = '6b7ed405-e8ef-4b8d-9515-f09108e9e367';

const getMediaCardBaseURL = (host = '') => {
  if (host.match(/theinfluencer/i)) {
    return 'https://share.theinfluencer.es';
  }
  if (host.match(/influxer/i)) {
    return 'https://brands.influxer.co.uk';
  }
  return 'https://app.dyzio.co';
};

const MediaKitLauncher = ({ influencer, mediaKitUrl }) => {
  const safeUrl = mediaKitUrl ? _.trimEnd(mediaKitUrl, '/') : getMediaCardBaseURL(window.origin);

  const url = `${safeUrl}/media-kit/${influencer.id}`;
  return (
    <Popup
      trigger={
        <Icon
          link
          onClick={() => window.open(url, '_blank')}
          name="address card outline"
          size="huge"
          style={dyzioPinkStyle}
        />
      }
      content={`Open Media Kit for ${influencer.name} (in a new window).`}
    />
  );
};

MediaKitLauncher.defaultProps = {
  mediaKitUrl: 'https://app.dyzio.co',
};

const InfluencerInfoBar = props => {
  const {
    influencer,
    influencerDetails,
    id,
    withBlogosphereTab,
    onADRModalStateChanged,
    organisationId,
    organisation,
  } = props;

  if (!influencer) return null;
  const {
    name,
    thumbnail,
    primarySocialHandle,
    facebookCount,
    instagramCount,
    twitterCount,
    youtubeCount,
    facebook,
    instagram,
    twitter,
    youtube,
    twitch,
    twitchCount,
    tiktok,
    tiktokCount,
    telegramUser,
  } = influencer;

  const {
    assessment,
  } = influencerDetails;

  let youtubeSlug;
  if (youtube) {
    const idRegEx = /^UC([a-zA-Z0-9_-]{22})$/;
    const isChannelId = yt => yt.match(idRegEx);
    youtubeSlug = isChannelId(youtube) ? `channel/${youtube}` : `@${youtube}`;
  }

  const commaFormat = num => {
    const format = num > 1000000 ? '0.0a' : '0a';
    return numeral(num).format(format);
  };

  const { dataPermission, isLoadingPermission } = props;
  const adrStatus = dataPermission && dataPermission.requestStatus;

  const proxyInfluencer = {
    youtubeCount,
    facebookCount,
    instagramCount,
    twitterCount,
  };

  const ADR_BUTTON_STATES = {
    APPROVED: {
      label: 'Approved',
      icon: 'checkmark',
      colour: 'green',
      disabled: true,
    },
    PENDING: {
      label: 'Pending',
      icon: 'clock',
      colour: 'orange',
      disabled: true,
    },
  };

  const displayAuthorisedData = _.get(organisation, 'displayAuthorisedData', false);

  const canOrgDisplay = _.includes(ALLOWED_ORGS, organisationId);
  const shouldRenderStories = displayAuthorisedData;

  return (
    <div>
      <div className="page-hero flex no-border">
        <div className="item">
          <Image src={thumbnail} alt={name} size="tiny" shape="circular" spaced="right" />
        </div>
        <div className="space">
          <h2>
            {name}&nbsp;&nbsp;<span className="light">{primarySocialHandle}</span>
          </h2>
          <div className="spacer small" />
          <Rating icon="star" rating={assessment} maxRating={5} disabled />
        </div>
        <div className="space">
          {
            !isLoadingPermission && canOrgDisplay &&
            <DyzioPrimaryButton
              label={_.get(ADR_BUTTON_STATES, `${adrStatus}.label`, 'Send ADR request')}
              color={_.get(ADR_BUTTON_STATES, `${adrStatus}.colour`, 'pink')}
              hasIcon
              disabled={_.get(ADR_BUTTON_STATES, `${adrStatus}.disabled`, false)}
              onClick={onADRModalStateChanged}
              icon={<Icon name={_.get(ADR_BUTTON_STATES, `${adrStatus}.icon`, 'send')} />}
            />
          }
        </div>
        <div className="item">
          <div className="flex">
            <div className="space align-center">
              <TargetLink
                href={`https://www.facebook.com/${facebook}`}
                renderIf={facebook}
              >
                <img src="/icons/facebook.svg" alt="Facebook" />
                { facebook && <p className="light">{commaFormat(facebookCount)}</p>}
                { !facebook && <p className="light"><Icon name="close" color="grey" /></p>}
              </TargetLink>
            </div>
            <div className="space align-center">
              <TargetLink
                href={`https://www.instagram.com/${instagram}`}
                renderIf={instagram}
              >
                <img src="/icons/instagram.svg" alt="Instagram" />
                { instagram && <p className="light">{commaFormat(instagramCount)}</p>}
                { !instagram && <p className="light"><Icon name="close" color="grey" /></p>}
              </TargetLink>
            </div>
            <div className="space align-center">
              <TargetLink
                href={`https://www.twitter.com/${twitter}`}
                renderIf={twitter}
              >
                <img src="/icons/twitter.svg" alt="Twitter" />
                { twitter && <p className="light">{commaFormat(twitterCount)}</p>}
                { !twitter && <p className="light"><Icon name="close" color="grey" /></p>}
              </TargetLink>
            </div>
            <div className="space align-center">
              <TargetLink
                href={`https://www.youtube.com/${youtubeSlug}`}
                renderIf={youtube}
              >
                <img src="/icons/youtube.svg" alt="YouTube" />
                { youtube && <p className="light">{commaFormat(youtubeCount)}</p>}
                { !youtube && <p className="light"><Icon name="close" color="grey" /></p>}
              </TargetLink>
            </div>

            <div className="space align-center">
              <TargetLink
                href={`https://www.twitch.com/${twitch}`}
                renderIf={twitch && twitchCount}
              >
                <Icon name="twitch" color="purple" />
                { twitch && <p className="light">{commaFormat(twitchCount)}</p>}
                { !twitch && <p className="light"><Icon name="close" color="grey" /></p>}
              </TargetLink>
            </div>
            <div className="space align-center">
              <TargetLink
                href={`https://www.tiktok.com/@${tiktok}`}
                renderIf={tiktok}
              >
                <img style={tiktokIconStyle} src="https://app.dyzio.co/logos/tiktok-logo.svg" alt="Tiktok" />
                { tiktok && <p className="light">{commaFormat(tiktokCount)}</p>}
                { !tiktok && <p className="light"><Icon name="close" color="grey" /></p>}
              </TargetLink>
            </div>
            <div className="space align-center">
              <TargetLink
                href={`https://www.facebook.com/${facebook}`}
                renderIf={false}
              >
                <Icon name="telegram" color="blue" />
                { telegramUser && <p className="light"><Icon name="check circle" color="green" /></p>}
                { !telegramUser && <p className="light"><Icon name="close" color="grey" /></p>}
              </TargetLink>
            </div>
            <div className="space" />
            <div className="item align-center">
              <p><b>Category</b></p>
              <InfluencerCategoryComponent influencer={proxyInfluencer} />
            </div>
            <div className="space" />
            <div className="item align-center">
              <MediaKitLauncher influencer={influencer} mediaKitUrl={organisation && organisation.mediaKitUrl} />
            </div>
          </div>
        </div>
      </div>
      <div className="flex-tabs">
        <div className="tab" style={tabStyle}>
          <h4>
            <Link style={simpleLinkStyle} to={`/influencer/${id}/statistics`} activeStyle={activeLinkStyle}>
              <b>Statistics</b>
            </Link>
          </h4>
        </div>
        <div className="tab" style={tabStyle}>
          <h4>
            <Link style={simpleLinkStyle} to={`/influencer/${id}/latest`} activeStyle={activeLinkStyle}>
              <b>Latest Posts</b>
            </Link>
          </h4>
        </div>
        <div className="tab" style={tabStyle}>
          <h4>
            <Link style={simpleLinkStyle} to={`/influencer/${id}/campaigns`} activeStyle={activeLinkStyle}>
              <b>Campaigns</b>
            </Link>
          </h4>
        </div>
        <div className="tab" style={tabStyle}>
          <h4>
            <Link style={simpleLinkStyle} to={`/influencer/${id}/profile`} activeStyle={activeLinkStyle}>
              <b>Profile</b>
            </Link>
          </h4>
        </div>
        {
          instagram && shouldRenderStories &&
          <div className="tab" style={tabStyle}>
            <h4>
              <Link style={simpleLinkStyle} to={`/influencer/${id}/stories`} activeStyle={activeLinkStyle}>
                <b>Stories</b>
              </Link>
            </h4>
          </div>
        }
        {
          withBlogosphereTab &&
          <div className="tab" style={tabStyle}>
            <h4>
              <Link style={simpleLinkStyle} to={`/influencer/${id}/blogosphere`} activeStyle={activeLinkStyle}>
                <b>Blogosphere</b>
              </Link>
            </h4>
          </div>
        }
        <div className="tab" style={tabStyle}>
          <h4>
            <Link style={simpleLinkStyle} to={`/influencer/${id}/rates`} activeStyle={activeLinkStyle}>
              <b>Rates</b>
            </Link>
          </h4>
        </div>
        <div className="tab" style={tabStyle}>
          <h4>
            <Link style={simpleLinkStyle} to={`/influencer/${id}/reviews`} activeStyle={activeLinkStyle}>
              <b>Notes & Reviews</b>
            </Link>
          </h4>
        </div>
        {
          organisationId === ABBOTTLYON &&
          <div className="tab" style={tabStyle}>
            <h4>
              <Link style={simpleLinkStyle} to={`/influencer/${id}/questionnaire`} activeStyle={activeLinkStyle}>
                <b>Questionnaire</b>
              </Link>
            </h4>
          </div>
        }
      </div>
    </div>
  );
};

InfluencerInfoBar.propTypes = {
  influencer: PropTypes.object.isRequired,
  id: PropTypes.string.isRequired,
  router: PropTypes.object.isRequired,
};

export default withRouter(InfluencerInfoBar);



/** WEBPACK FOOTER **
 ** ./src/components/influencer-info-bar/index.js
 **/